/** Игры и бонусы витрины: вкладка, хаб и экран одной игры.
 *
 * Раздел живёт рядом с каталогом, а не внутри него: заказ — работа, игра —
 * подарок, и мешать их нельзя. Отсюда вкладка сбоку только на главной, отсюда
 * же отдельный стек экранов.
 *
 * Ответ сервера кэшируется на модуле: вкладке нужно знать «включено ли»,
 * хабу — список, экрану игры — конкретную карточку, и все трое должны видеть
 * одни и те же остатки попыток после каждой прокрутки.
 */
import { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { ApiError, request } from '@/shared/api';
import type { BuyResult, GameCard, GamesView, PlayResult, ZigzagTop } from '@/shared/types';
import { Button, Note, Skeleton } from '@/shared/ui';
import { CoinIcon } from '@/shared/coin';
import { ErrorBoundary } from '@/shared/ErrorBoundary';
import { tap } from '@/shared/tg';
import { count } from '@/shared/plural';
import type { Screen } from './nav';

/** Сами игры — трёхмерные сцены на three.js, и весят они больше всей
    витрины. Грузятся по требованию: чанки уезжают отдельными файлами и едут
    только тому, кто действительно открыл игру. */
const ChestGame = lazy(() => import('./chests'));
const ZigzagGame = lazy(() => import('./zigzag'));

/** Последний ответ /api/games: общий для вкладки, хаба и экрана игры. */
let cached: GamesView | null = null;

/** Карточка игры из кэша — для ленивого экрана сундуков. */
export function gameFromCache(id: string): GameCard | undefined {
  return cached?.games.find((item) => item.id === id);
}

/** Запомнить итог попытки: хаб и вкладка должны видеть свежие остатки. */
export function rememberPlay(id: string, answer: PlayResult): void {
  const mirror = cached?.games.find((item) => item.id === id);
  if (!mirror) return;
  mirror.wait = answer.next_in;
  mirror.left = answer.left;
  mirror.bonus = answer.bonus;
  mirror.paid = answer.paid ?? mirror.paid;
  mirror.buys_left = answer.buys_left ?? mirror.buys_left;
  rememberBalance(answer.balance);
}

/** Баланс паркоинов, каким его в последний раз видела витрина: кнопке покупки
    нужно знать, хватает ли, ещё до нажатия. */
export function knownBalance(): number {
  return cached?.balance ?? 0;
}

export function rememberBalance(balance: number): void {
  if (cached && Number.isFinite(balance)) cached.balance = balance;
}

/* Живых механик две. Колесо, слоты, скретч, кубик и монетка сняты: сервер
   их больше не присылает, а их сцены удалены вместе со стилями. */
const GAME_ICON: Record<string, string> = { cases: '🎁', zigzag: '🌀' };

/* Коротко и без хвостов: «Через 24 ч 0 мин» не влезало в кнопку на телефоне
   и обрезалось посередине. Одна величина вместо двух читается быстрее. */
export function waitLabel(seconds: number): string {
  if (seconds < 0) return 'Уже сыграно';
  if (seconds <= 0) return 'Можно играть';
  if (seconds < 90) return 'Ещё минуту';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `Ещё ${minutes} мин`;
  const hours = Math.round(seconds / 3600);
  if (hours < 24) return `Ещё ${hours} ч`;
  return `Ещё ${Math.round(seconds / 86400)} дн`;
}

/** Сколько попыток осталось — словами, если это вообще ограничено. */
export function attemptsLabel(game: GameCard): string {
  const bonus = Number(game.bonus) || 0;
  const paid = Number(game.paid) || 0;
  // Подарок или покупка ломают счёт «N из M» — тогда честнее просто число
  if (bonus || paid) {
    const notes = [
      bonus ? `подаренных: ${bonus}` : '',
      paid ? `купленных: ${paid}` : '',
    ].filter(Boolean);
    return `Осталось попыток: ${game.left} (из них ${notes.join(', ')})`;
  }
  if (game.one_shot) return 'Одна попытка за всё время';
  if (!game.attempts || game.attempts <= 1) return '';
  return `Осталось попыток: ${game.left} из ${game.attempts}`;
}

/** Состояние раздела для нижней навигации: включён ли и есть ли что играть. */
export async function loadGamesView(): Promise<GamesView> {
  const answer = await request<GamesView>('/api/games', {});
  cached = answer;
  return answer;
}

/* ------------------------------------------------------- попытка за монеты */

/** Кнопка «купить попытку»: появляется, когда попытки кончились, а игра их
    продаёт. Сама ходит на сервер, а наверх отдаёт готовый ответ — экран игры
    из него обновляет остатки и открывает сцену. */
export function BuyButton({
  game,
  onBought,
  onToast,
}: {
  game: GameCard;
  onBought: (answer: BuyResult) => void;
  onToast: (text: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [balance, setBalance] = useState(knownBalance);
  const price = game.price ?? 0;
  if (price <= 0 || game.wait === 0) return null;
  if (game.buys_left === 0) {
    return <div className="text-center text-[12px] text-hint">Покупок на сегодня больше нет</div>;
  }
  const short = balance < price;

  const buy = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const answer = await request<BuyResult>('/api/games/buy', { game_id: game.id });
      rememberBalance(answer.balance);
      setBalance(answer.balance);
      const mirror = cached?.games.find((item) => item.id === game.id);
      if (mirror) {
        mirror.wait = answer.next_in;
        mirror.left = answer.left;
        mirror.paid = answer.paid;
        mirror.buys_left = answer.buys_left;
      }
      tap('success');
      onToast('Попытка куплена — играйте!');
      onBought(answer);
    } catch (error) {
      const why = (error as ApiError).message;
      if (why === 'not_enough') onToast('Не хватает паркоинов');
      else if (why === 'buy_limit') onToast('Покупок на сегодня больше нет');
      else if (why === 'have_attempts') onToast('У вас ещё есть попытка');
      else onToast(`Не вышло: ${why}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-1">
      <Button
        tone="accent"
        disabled={busy || short}
        onClick={() => void buy()}
        className="flex w-full items-center justify-center gap-1.5"
      >
        <CoinIcon size={16} />
        <span data-numeric>Купить попытку · {price}</span>
      </Button>
      {short && (
        <div className="text-center text-[12px] text-hint" data-numeric>
          Не хватает: у вас {balance} из {price}
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------- хаб */

/** Экран «скоро» и «ещё рано» — одна форма: круг, обещание, объяснение. */
function SoonCard({ icon, title, text }: { icon: string; title: string; text: string }) {
  return (
    <div className="px-4 pt-8 pb-2 text-center">
      <div className="soon-orb">
        <span>{icon}</span>
      </div>
      <h2 className="mt-5 mb-2 text-[19px] font-[700] tracking-[-.02em]">{title}</h2>
      <p className="m-0 text-sm text-hint">{text}</p>
    </div>
  );
}

/** Почему раздел закрыт — человеческими словами, а не кодом причины. */
function deniedText(reason: string): string {
  if (reason.startsWith('too_new:')) {
    return `Играть можно тем, кто с нами дольше. Загляните через ${reason.split(':')[1]} дн.`;
  }
  if (reason.startsWith('need_orders:')) {
    const need = Number(reason.split(':')[1] ?? 0);
    return `Игры открываются после ${count(need, 'заказа', 'заказов', 'заказов')}.`;
  }
  if (reason === 'unknown_user') return 'Напишите боту в чат — и раздел откроется.';
  if (reason.startsWith('lapsed:')) return 'Игры откроются после нового заказа.';
  if (reason === 'need_purchase') {
    return 'Сделайте любой заказ — и игры ваши. Выигранные паркоины тратятся на скидки и подарки.';
  }
  return 'Раздел пока недоступен.';
}

export function GamesHub({
  onOpen,
  visible = true,
}: {
  onOpen: (screen: Screen) => void;
  /** Вкладку видно по-настоящему, а не краем глаза во время листания. */
  visible?: boolean;
}) {
  // Пока листают, показываем то, что уже знаем с загрузки витрины: подсмотр
  // должен быть похож на место, куда едешь
  const [view, setView] = useState<GamesView | null>(cached);
  const [failure, setFailure] = useState<string | null>(null);
  const counted = useRef(false);

  useEffect(() => {
    // Заход считаем, когда до раздела действительно дошли. Раньше это делал
    // сам факт появления в разметке — но с листанием пальцем экран возникает
    // и у того, кто просто провёл по краю и передумал
    if (!visible || counted.current) return;
    counted.current = true;
    // На медленном канале человек успевает уйти в игру, пока грузится список.
    // Опоздавший ответ не должен затирать экран, который он уже открыл.
    let alive = true;
    // source: open — это и есть заход в раздел, его считает статистика
    request<GamesView>('/api/games', { source: 'open' })
      .then((answer) => {
        cached = answer;
        if (alive) setView(answer);
      })
      .catch((error: ApiError) => {
        if (alive) setFailure(error.message);
      });
    return () => {
      alive = false;
    };
  }, [visible]);

  if (failure) return <Note tone="warn">Не открылось: {failure}</Note>;
  if (!view) return <Skeleton className="h-16" />;

  if (view.teaser) {
    return (
      <SoonCard
        icon="🎁"
        title={view.teaser_text || 'Скоро здесь будет много интересного'}
        text="Загляните позже — подарки уже готовятся."
      />
    );
  }

  if (!view.allowed) {
    // Давно не заказывал — тоже приглашение, а не замок: текст пишет админ
    if (view.denied.startsWith('lapsed')) {
      return (
        <SoonCard
          icon="🛍️"
          title="Игры ждут нового заказа"
          text={view.denied_text || deniedText(view.denied)}
        />
      );
    }
    // Не купившим — не замок, а приглашение: игры открываются покупкой
    return view.denied === 'need_purchase' ? (
      <SoonCard
        icon="🛍️"
        title="Игры — для покупателей"
        text={deniedText(view.denied)}
      />
    ) : (
      <SoonCard icon="🔒" title="Ещё рано" text={deniedText(view.denied || '')} />
    );
  }

  if (!view.games.length) return <Note>Игр пока нет.</Note>;

  return (
    <>
      {view.preview && (
        // Видит только админ: без напоминания легко забыть, что раздел
        // работает лишь для своих, а клиенты всё ещё смотрят на «скоро»
        <Note>Режим обкатки: клиенты сейчас видят «скоро», играют только админы.</Note>
      )}
      <div className="grid grid-cols-2 gap-2.5">
        {view.games.map((game, index) => {
          const ready = game.wait === 0;
          return (
            <button
              key={game.id}
              type="button"
              onClick={() => {
                tap();
                onOpen({ type: 'game', id: game.id, title: game.title });
              }}
              style={{ animationDelay: `${index * 28}ms` }}
              className={[
                'glass wet-edge animate-rise flex flex-col items-start gap-[3px] rounded-card',
                'px-3.5 pt-[15px] pb-[13px] text-left transition-[transform,border-color]',
                'duration-150 ease-glass active:scale-[.97] active:border-gold',
                // Недоступная игра гаснет: видно с одного взгляда, во что можно
                // играть прямо сейчас
                ready ? '' : 'opacity-55',
              ].join(' ')}
            >
              <span className={`mb-1 text-[27px] leading-[1.1] ${ready ? '' : 'grayscale'}`}>
                {GAME_ICON[game.kind] ?? '🎮'}
              </span>
              <span
                className={`text-[15px] font-[680] tracking-[-.01em] ${ready ? '' : 'text-hint'}`}
              >
                {game.title}
              </span>
              <span className="text-xs text-hint">{game.subtitle}</span>
              <span
                className={[
                  'mt-[7px] inline-flex items-center gap-[5px] text-[11px] font-[620]',
                  ready ? 'text-gold-2' : 'text-hint',
                ].join(' ')}
                data-numeric
              >
                {ready && <i className="state-dot" />}
                {waitLabel(game.wait)}
              </span>
            </button>
          );
        })}
      </div>
      <Note>Выигрыш — паркоины: капают в баланс сразу и тратятся в магазине бонусов.</Note>
      <HubLeaders games={view.games} onOpen={onOpen} />
    </>
  );
}

/** Лидеры зигзага прямо в разделе: таблица — часть витрины, а не секрет
    внутри игры. Тап по ней ведёт в саму игру. */
function HubLeaders({
  games,
  onOpen,
}: {
  games: GameCard[];
  onOpen: (screen: Screen) => void;
}) {
  const zigzag = games.find((game) => game.kind === 'zigzag');
  const [board, setBoard] = useState<ZigzagTop | null>(null);

  useEffect(() => {
    if (!zigzag) return;
    let alive = true;
    request<ZigzagTop>('/api/games/zigzag/top', { game_id: zigzag.id })
      .then((answer) => {
        if (alive) setBoard(answer);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [zigzag?.id]);

  if (!zigzag || !board || board.top.length === 0) return null;

  return (
    <button
      type="button"
      className="glass wet-edge mt-1 w-full overflow-hidden rounded-card text-left"
      onClick={() => {
        tap();
        onOpen({ type: 'game', id: zigzag.id, title: zigzag.title });
      }}
    >
      <div className="flex items-baseline gap-2 px-3.5 pt-3 pb-1.5">
        <span className="text-[14px] font-[700]">
          🏆 {board.scope === 'week' ? 'Лига недели' : 'Лидеры'} «{zigzag.title}»
        </span>
        <span className="text-[11px] text-hint">
          {board.board === 'jars' ? 'банки за забег' : 'очки'}
        </span>
        {board.me.rank !== null && (
          <span className="ml-auto text-[12px] text-hint" data-numeric>
            вы {board.me.rank}-й
          </span>
        )}
      </div>
      {board.top.slice(0, 5).map((row, at) => (
        <div
          key={row.user_id}
          className={`flex items-baseline gap-2.5 px-3.5 py-1.5 ${row.me ? 'bg-gold/10' : ''}`}
        >
          <span className={`w-5 shrink-0 text-[12px] font-[700] ${at < 3 ? 'text-gold-2' : 'text-hint'}`} data-numeric>
            {at + 1}
          </span>
          <span className={`min-w-0 flex-1 truncate text-[13.5px] ${row.me ? 'font-[700] text-gold-2' : ''}`}>
            {row.me ? 'Вы' : row.name}
            {row.username && <span className="font-[400] text-hint"> @{row.username}</span>}
          </span>
          <span className="shrink-0 text-[13.5px] font-[720]" data-numeric>
            {row.value}
          </span>
        </div>
      ))}
      <div className="px-3.5 pt-1 pb-3 text-[12px] text-link">Сыграть →</div>
    </button>
  );
}

/* -------------------------------------------------------------- одна игра */

/** Экран одной игры: тонкая обёртка над ленивыми сундуками.
 *
 * Пока едет чанк со сценой, на его месте стоит скелет той же высоты — экран
 * не прыгает, когда сцена приезжает.
 */
export function GameScreen({
  gameId,
  onMissing,
  onToast,
}: {
  gameId: string;
  onMissing: () => void;
  onToast: (text: string) => void;
}) {
  const kind = gameFromCache(gameId)?.kind ?? 'cases';
  const Stage = kind === 'zigzag' ? ZigzagGame : ChestGame;
  // Сцена падает в своей рамке: вебвью без WebGL или чанк после выката
  // не должны уносить с собой каталог и корзину
  return (
    <ErrorBoundary title="Игра не открылась">
      <Suspense fallback={<Skeleton className="mt-[14px] h-[min(58vh,520px)] rounded-card" />}>
        <Stage gameId={gameId} onMissing={onMissing} onToast={onToast} />
      </Suspense>
    </ErrorBoundary>
  );
}
